import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import {
  LayoutDashboard, GraduationCap, Building2, Users, Presentation, UserCog,
  Landmark, CalendarDays, ListChecks, FolderArchive, BarChart3, Settings,
  LogOut, Menu, Search, Bell, X,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import GlobalSearch from "@/components/GlobalSearch";
import NotificationsBell from "@/components/NotificationsBell";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Dashboard", id: "dashboard", icon: LayoutDashboard, roles: "all" },
  { to: "/corsi", label: "Corsi", id: "corsi", icon: GraduationCap, roles: "all" },
  { to: "/aziende", label: "Aziende", id: "aziende", icon: Building2, roles: "internal" },
  { to: "/persone", label: "Persone", id: "persone", icon: Users, roles: "internal" },
  { to: "/docenti", label: "Personale (docenti e tutor)", id: "docenti", icon: Presentation, roles: "internal" },
  { to: "/personale", label: "Personale INDAR", id: "personale", icon: UserCog, roles: ["admin", "amministrazione", "direzione"] },
  { to: "/finanziamenti", label: "Finanziamenti", id: "finanziamenti", icon: Landmark, roles: ["admin", "amministrazione", "direzione"] },
  { to: "/calendario", label: "Calendario", id: "calendario", icon: CalendarDays, roles: "all" },
  { to: "/attivita", label: "Attività", id: "attivita", icon: ListChecks, roles: "internal" },
  { to: "/documenti", label: "Documenti", id: "documenti", icon: FolderArchive, roles: "internal" },
  { to: "/report", label: "Report & Costi", id: "report", icon: BarChart3, roles: ["admin", "amministrazione", "direzione"] },
  { to: "/amministrazione", label: "Amministrazione", id: "amministrazione", icon: Settings, roles: ["admin"] },
];

const INTERNAL = ["admin", "amministrazione", "didattica", "direzione"];

function canSee(item, role) {
  if (item.roles === "all") return true;
  if (item.roles === "internal") return INTERNAL.includes(role);
  return item.roles.includes(role);
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || user?.email || "?")
    .split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const sidebarEl = (testid) => (
    <aside className="flex h-full w-64 flex-col bg-[#0F172A] text-slate-300" data-testid={testid}>
      <div className="flex items-center gap-2 px-5 h-16 border-b border-white/5">
        <Logo variant="light" />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
        {NAV.filter((i) => canSee(i, user?.role)).map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-${item.id}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#1E293B] text-white"
                    : "text-slate-400 hover:bg-white/5 hover:text-slate-100"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className={`h-[18px] w-[18px] ${isActive ? "text-[#E11D48]" : ""}`} />
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-white/5">
        <div className="flex items-center gap-3 rounded-lg px-2 py-2">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-[#E11D48] text-white text-xs font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-slate-100">{user?.name || "Utente"}</p>
            <p className="truncate text-xs text-slate-500">{user?.role_label}</p>
          </div>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <div className="hidden lg:block">{sidebarEl("sidebar-desktop")}</div>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{sidebarEl("sidebar-mobile")}</div>
          <button onClick={() => setMobileOpen(false)} className="absolute left-[16.5rem] top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-700 shadow" data-testid="mobile-menu-close" aria-label="Chiudi menu">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 items-center gap-2 border-b bg-white px-3 sm:gap-3 sm:px-4 lg:px-6">
          <button
            className="lg:hidden flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </button>
          <GlobalSearch className="min-w-0 flex-1 sm:max-w-sm" />
          <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2">
            <NotificationsBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-lg p-1.5 hover:bg-slate-100" data-testid="user-menu-btn">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-slate-900 text-white text-xs">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-medium">{user?.name}</div>
                  <div className="text-xs text-slate-500">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-rose-600">
                  <LogOut className="mr-2 h-4 w-4" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-6" data-testid="main-content">
          <div className="mx-auto max-w-[1400px] animate-in-up">{children}</div>
        </main>
      </div>
    </div>
  );
}
